/**
 * ESQUEMA CONGELADO — schemaVersion 2.
 *
 * Igual que `v1.ts`: no importa nada de `layers.ts` ni de
 * `canonical-document.ts` A PROPOSITO. Una version antigua no puede quedar a
 * merced de la evolucion de la actual. Si este fichero se edita, deja de poder
 * abrirse un documento v2 real, y en produccion los hay. NO SE TOCA.
 *
 * Diferencias con v3, que son las que migra `v2-to-v3.ts`:
 *  - `font` no lleva `weight` ni `italic` (solo habia un fichero por familia).
 *  - no hay `align`, `textCase`, `outline` ni `arc`.
 */
import { z } from 'zod';

const MillimetersV2 = z.number().finite();
const PointMmV2 = z.object({ x: MillimetersV2, y: MillimetersV2 });

const AffineMatrixV2 = z.object({
  a: z.number().finite(),
  b: z.number().finite(),
  c: z.number().finite(),
  d: z.number().finite(),
  e: MillimetersV2,
  f: MillimetersV2,
});

const ColorV2 = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('rgb'),
    r: z.number().int().min(0).max(255),
    g: z.number().int().min(0).max(255),
    b: z.number().int().min(0).max(255),
  }),
  z.object({
    type: z.literal('cmyk'),
    c: z.number(),
    m: z.number(),
    y: z.number(),
    k: z.number(),
  }),
  z.object({
    type: z.literal('spot'),
    book: z.string(),
    code: z.string(),
    preview: z.object({ r: z.number(), g: z.number(), b: z.number() }),
  }),
  z.object({ type: z.literal('technical_white') }),
]);

const LayerBaseV2 = z.object({
  id: z.string().uuid(),
  name: z.string().max(120).optional(),
  z: z.number().int(),
  transform: AffineMatrixV2,
  anchor: z.enum(['top-left', 'center', 'custom']),
  anchorPoint: PointMmV2.optional(),
  opacity: z.number().min(0).max(1).default(1),
  blend: z.enum(['normal', 'multiply', 'screen']).default('normal'),
  clip: z.object({ polygonMm: z.array(PointMmV2).min(3) }).optional(),
  locked: z.boolean().default(false),
});

export const TextLayerV2 = LayerBaseV2.extend({
  kind: z.literal('text'),
  text: z.string(),
  font: z.object({ family: z.string(), version: z.string(), fileHash: z.string() }),
  sizeMm: MillimetersV2.refine((v) => v > 0, 'sizeMm debe ser > 0'),
  tracking: z.number(),
  lineHeightMm: MillimetersV2.refine((v) => v > 0, 'lineHeightMm debe ser > 0'),
  direction: z.enum(['ltr', 'rtl']).default('ltr'),
  color: ColorV2,
  frozenLayout: z
    .object({
      engine: z.string(),
      engineVersion: z.string(),
      glyphs: z.array(z.object({ id: z.number(), xMm: MillimetersV2, yMm: MillimetersV2 })),
      variationAxes: z.record(z.number()).optional(),
      substitutions: z.array(z.string()).optional(),
      fallbackFontHash: z.string().optional(),
      approvedOutlineAssetId: z.string().optional(),
    })
    .optional(),
});

export const ImageLayerV2 = LayerBaseV2.extend({
  kind: z.literal('image'),
  assetChecksum: z.string(),
  intrinsic: z.object({
    widthPx: z.number(),
    heightPx: z.number(),
    dpi: z.number().optional(),
  }),
});

export const ShapeLayerV2 = LayerBaseV2.extend({
  kind: z.literal('shape'),
  path: z.string(),
  fill: ColorV2.optional(),
  stroke: ColorV2.optional(),
  strokeWidthMm: MillimetersV2.optional(),
});

export const CanonicalDocumentV2 = z.object({
  schemaVersion: z.literal(2),
  origin: z.object({ zoneId: z.string().min(1), axis: z.enum(['y-down', 'y-up']).default('y-down') }),
  canvasMm: z.object({
    width: MillimetersV2.refine((v) => v > 0, 'width debe ser > 0'),
    height: MillimetersV2.refine((v) => v > 0, 'height debe ser > 0'),
  }),
  layers: z.array(z.discriminatedUnion('kind', [TextLayerV2, ImageLayerV2, ShapeLayerV2])),
});

export type CanonicalDocumentV2 = z.infer<typeof CanonicalDocumentV2>;
